import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  design_flow_m3hr: '',
  number_of_vessels: '1',
  vessel_construction: 'frp',
  raw_water_hardness_ppm_as_caco3: '',
  run_length_hours: '24',
  regeneration_level: 'conventional',
  resin_capacity_g_per_liter: '',
  max_service_velocity_m_hr: '40',
  backwash_rate_m_hr: '10',
  backwash_duration_min: '10',
};

const SoftenerCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.design_flow_m3hr || !form.raw_water_hardness_ppm_as_caco3) {
      showToast('Design flow and raw water hardness are required', 'error');
      return null;
    }
    return {
      design_flow_m3hr: Number(form.design_flow_m3hr),
      number_of_vessels: Number(form.number_of_vessels),
      vessel_construction: form.vessel_construction,
      raw_water_hardness_ppm_as_caco3: Number(form.raw_water_hardness_ppm_as_caco3),
      run_length_hours: Number(form.run_length_hours),
      regeneration_level: form.regeneration_level,
      resin_capacity_g_per_liter: form.resin_capacity_g_per_liter ? Number(form.resin_capacity_g_per_liter) : null,
      max_service_velocity_m_hr: Number(form.max_service_velocity_m_hr),
      backwash_rate_m_hr: Number(form.backwash_rate_m_hr),
      backwash_duration_min: Number(form.backwash_duration_min),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('softener', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'softener',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Softener (Packed Bed)</h1>
        <p className="text-xs text-gray-500 mb-6">
          Resin volume sized from hardness load ÷ regeneration cycle. Vessel is selected purely by
          volume (no bed depth input) — depth is derived, velocity is a cross-check only — see
          "Assumptions" below the result.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Flow (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.design_flow_m3hr}
                  onChange={handleChange('design_flow_m3hr')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raw Hardness (ppm as CaCO₃) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.raw_water_hardness_ppm_as_caco3}
                  onChange={handleChange('raw_water_hardness_ppm_as_caco3')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run Length (hr)</label>
                <input
                  type="number"
                  step="any"
                  value={form.run_length_hours}
                  onChange={handleChange('run_length_hours')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Default: 24hr (daily regen)"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"># of Vessels</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.number_of_vessels}
                  onChange={handleChange('number_of_vessels')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Construction</label>
                <select
                  value={form.vessel_construction}
                  onChange={handleChange('vessel_construction')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="frp">FRP (standard sizes)</option>
                  <option value="ms_fabricated">MS (fabricated, round to 50mm)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Regeneration Level</label>
                <select
                  value={form.regeneration_level}
                  onChange={handleChange('regeneration_level')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="conventional">Conventional (120 g NaCl/L)</option>
                  <option value="high_efficiency">High-Efficiency (160 g NaCl/L)</option>
                </select>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-xs font-semibold text-komal-blue hover:underline"
              >
                {showAdvanced ? '− Hide' : '+ Show'} resin, velocity &amp; backwash parameters (defaults: 58 g CaCO₃/L resin capacity / 40 m/hr max velocity cross-check / 10 m/hr backwash / 10 min; resin is a packed bed, no freeboard)
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Resin Capacity (g CaCO₃/L, override)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.resin_capacity_g_per_liter}
                      onChange={handleChange('resin_capacity_g_per_liter')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Default: 58 (conventional) / 64 (high-eff.)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Max Service Velocity (m/hr, cross-check)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.max_service_velocity_m_hr}
                      onChange={handleChange('max_service_velocity_m_hr')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Rate (m/hr)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.backwash_rate_m_hr}
                      onChange={handleChange('backwash_rate_m_hr')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Backwash Duration (min)</label>
                    <input
                      type="number"
                      step="any"
                      value={form.backwash_duration_min}
                      onChange={handleChange('backwash_duration_min')}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Total Resin Volume" value={`${result.outputs.total_required_resin_volume_liters} L`} highlight />
              <Stat
                label="Diameter (selected)"
                value={`${result.outputs.diameter_recommended_mm} mm (${result.outputs.diameter_recommended_inches})`}
                highlight
              />
              <Stat label="Bed Depth (derived)" value={`${result.outputs.actual_bed_depth_mm} mm`} />
              <Stat label="Actual Velocity (cross-check)" value={`${result.outputs.actual_velocity_m_hr} m/hr`} />
              <Stat label="Required Internal Height" value={`${result.outputs.required_internal_height_mm} mm`} />
              <Stat
                label={result.outputs.height_recommended_mm != null ? 'Vessel Height (catalog)' : 'Vessel Height (fabricated)'}
                value={
                  result.outputs.height_recommended_inches
                    ? `${result.outputs.overall_height_mm} mm (${result.outputs.height_recommended_inches})`
                    : `${result.outputs.overall_height_mm} mm`
                }
                highlight={result.outputs.vessel_fits}
              />
              {!result.outputs.vessel_fits && (
                <Stat label="Fits Catalog?" value="No — see warning above" highlight />
              )}
              <Stat label="Salt / Regeneration" value={`${result.outputs.salt_kg_per_regeneration_total} kg`} highlight />
              <Stat label="Regenerations / Day" value={result.outputs.regenerations_per_day} />
              <Stat label="Salt / Day" value={`${result.outputs.salt_kg_per_day_total} kg`} highlight />
              <Stat label="Backwash Flow" value={`${result.outputs.backwash_flow_m3hr} m³/hr`} />
              <Stat label="Backwash Volume" value={`${result.outputs.backwash_volume_liters.toLocaleString()} L`} />
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default SoftenerCalculatorPage;
